import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Crown } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { TRIBAL_STRUCTURE } from "@/data/content";

const Summary = ({ open, text }) => (
  <AnimatePresence initial={false}>
    {open && (
      <motion.p
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="font-sans text-sm text-[#A19D98] leading-relaxed mt-3 max-w-2xl overflow-hidden"
      >
        {text}
      </motion.p>
    )}
  </AnimatePresence>
);

const Heading = ({ node, depth, open }) => (
  <div className="flex-1">
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h3 className={`font-serif ${depth === 0 ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"} text-[#F5F0EB] group-hover:text-gold-text transition-colors`}>{node.title}</h3>
      <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-gold/60">{node.role}</span>
    </div>
    <Summary open={open} text={node.summary} />
  </div>
);

const Bullet = ({ open, depth }) => (
  <span className={`mt-1 flex items-center justify-center w-9 h-9 shrink-0 border transition-colors duration-300 ${open ? "border-gold bg-gold/10" : "border-gold/30"}`}>
    {depth === 0 ? <Crown size={16} className="text-gold" strokeWidth={1.4} /> : <ChevronRight size={15} className={`text-gold transition-transform duration-300 ${open ? "rotate-90" : ""}`} />}
  </span>
);

export default function TribalStructure({ embedded }) {
  const root = TRIBAL_STRUCTURE;
  const [openTop, setOpenTop] = useState(true);
  const [openMap, setOpenMap] = useState({});
  const toggle = (id) => setOpenMap((m) => ({ ...m, [id]: !m[id] }));

  return (
    <div>
      {!embedded && (
        <PageHeader
          eyebrow="Core Tribal Structure"
          title="The governance of a decentralized sovereign bureau."
          subtitle="Explore the institutional architecture of the Trust — an interactive map of the Council, its offices, the Modern Freedmen Bureau, and the regional chapters. Select any body to reveal its charter."
        />
      )}

      <section className="py-20 bg-ink">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="border border-gold/20 bg-ink-surface p-6 sm:p-10" data-testid="tribal-tree">
              {/* Level 0 — Council */}
              <motion.button
                type="button"
                onClick={() => setOpenTop(!openTop)}
                data-testid={`tribal-node-${root.id}`}
                whileHover={{ x: 3 }}
                transition={{ duration: 0.2 }}
                className="group w-full text-left flex items-start gap-4 py-5"
              >
                <Bullet open={openTop} depth={0} />
                <Heading node={root} depth={0} open={openTop} />
              </motion.button>

              {/* Level 1 — Offices */}
              <AnimatePresence initial={false}>
                {openTop && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                    {root.children.map((office) => (
                      <div key={office.id} className="border-l border-gold/15 pl-5 sm:pl-8">
                        <motion.button
                          type="button"
                          onClick={() => toggle(office.id)}
                          data-testid={`tribal-node-${office.id}`}
                          whileHover={{ x: 3 }}
                          transition={{ duration: 0.2 }}
                          className="group w-full text-left flex items-start gap-4 py-5"
                        >
                          <Bullet open={!!openMap[office.id]} depth={1} />
                          <Heading node={office} depth={1} open={!!openMap[office.id]} />
                        </motion.button>

                        {/* Level 2 — Chapters */}
                        <AnimatePresence initial={false}>
                          {openMap[office.id] && office.children && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                              {office.children.map((chapter) => (
                                <div key={chapter.id} className="border-l border-gold/15 pl-5 sm:pl-8">
                                  <motion.button
                                    type="button"
                                    onClick={() => toggle(chapter.id)}
                                    data-testid={`tribal-node-${chapter.id}`}
                                    whileHover={{ x: 3 }}
                                    transition={{ duration: 0.2 }}
                                    className="group w-full text-left flex items-start gap-4 py-5"
                                  >
                                    <Bullet open={!!openMap[chapter.id]} depth={2} />
                                    <Heading node={chapter} depth={2} open={!!openMap[chapter.id]} />
                                  </motion.button>
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold/50 mt-10 text-center">
              Preservation • Stewardship • Prosperity
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-ink-deep" data-testid="council-house-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>The Council House</Eyebrow>
            <h2 className="font-serif font-light text-4xl tracking-tight mt-5 max-w-3xl">
              Where the Council of Seven Generations convenes.
            </h2>
            <p className="font-sans text-[#A19D98] leading-relaxed mt-6 max-w-3xl">
              A council house reimagined for a sovereign future — the tiered circle gathered around a sacred
              central fire, in the enduring architectural tradition of the Southeastern Woodlands.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <figure className="border border-gold/20 bg-ink-surface mt-10 overflow-hidden">
              <img
                src="/council-house.jpg"
                alt="A futuristic Southeastern Woodlands tribal council house with a smoldering sacred fire at its center"
                data-testid="council-house-image"
                className="w-full h-auto object-contain"
              />
              <figcaption className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold/50 p-6 border-t border-gold/10">
                The Council House · Southeastern Woodlands tradition, reimagined
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
